/** Strings of the direction screens; merged into `ca` by src/i18n/ca.ts. */
export const ca_direction = {
  /** Nested under a name distinct from the top-level `ca.direction` string (the page title) and
   * from the area's other older top-level keys (ca.reveal, ca.health…). */
  directionPage: {
    /* ---- The night's own clock, and what a failure asks of whoever directs. */
    clock: (minutes: number, phase: string) =>
      phase === 'expired'
        ? 'S’ha exhaurit el temps previst de la missió. Continua fins que decideixis acabar-la.'
        : `Queden ${minutes} min del temps previst (${phase}).`,
    failedTitle: 'Objectius perduts',
    failedHint:
      'Una fallada ja no acaba la nit tota sola. Decideix tu: acaba-la, o obre un objectiu de recuperació al lloc on s’ha perdut.',
    recover: 'Obre una recuperació',
    recoverTitle: (what: string) => `Recupera el que quedi de: ${what}`,
    built: (objectives: number, forces: number, slots: number) =>
      `El servidor va muntar ${objectives} objectius, ${forces} elements i ${slots} places.`,

    twists: 'Girs',
    twistsHint:
      'Els girs que porta la missió. Tots es disparen sols al seu moment; amb aquests botons els pots avançar.',
    twistByHand: 'Teu d’avançar; si no, es dispara sol',
    twistOnItsOwn: 'Es dispara sol',
    serverFps: 'FPS del servidor',
    missionUnread: (player: string) =>
      `${player} no ha pogut llegir el fitxer de la missió (missionVersion -1): el seu joc s’ha quedat a l’illa que tenia carregada. Que surti i torni a entrar; si es repeteix, envia el seu RPT.`,
    selfCheck: 'Autoexamen del runtime:',
    selfCheckClean: (checked: number) => `cap problema en ${checked} coses mirades.`,
    selfCheckCount: (n: number) => (n === 1 ? '1 problema.' : `${n} problemes.`),
    selfCheckIssue: (kind: string, id: string, position: number[], height: number) => {
      const what: Record<string, string> = {
        unitInWater: 'un jugador a l’aigua',
        unitInAir: 'un jugador a l’aire',
        unitOffMap: 'un jugador fora del mapa',
        vehicleInWater: 'un vehicle a l’aigua',
        vehicleInAir: 'un vehicle a l’aire',
        vehicleOffMap: 'un vehicle fora del mapa',
        vehicleDestroyed: 'un vehicle destruït en començar',
        objectInAir: 'un objecte de la base a l’aire',
        objectOnWater: 'un objecte de la base sobre l’aigua',
        objectiveOnWater: 'un objectiu sobre l’aigua',
        objectiveOffMap: 'un objectiu fora del mapa',
        pointOnWater: 'un punt sobre l’aigua',
        pointOffMap: 'un punt fora del mapa',
      };
      const where = position.length >= 2 ? ` a [${position[0]}, ${position[1]}]` : '';
      const up = height > 0 ? `, ${height} m` : '';
      return `${what[kind] ?? kind}: ${id}${where}${up}`;
    },
    materialise: 'Materialitza l’enemic',
    materialiseOff: 'Torna a virtualitzar',
    materialiseHint:
      'Per veure’l des de Zeus abans que els jugadors hi arribin; el pressupost de grups actius es respecta igual.',
    envTitle: 'Temps i hora',
    envHint:
      'Només s’aplica el que omplis. El rellotge només va endavant: una hora ja passada és la de demà.',
    envOvercast: 'Núvols (%)',
    envFog: 'Boira (%)',
    envHour: 'Hora del dia',
    envMultiplier: 'Velocitat del rellotge',
    envSend: 'Aplica',
    enemyTitle: 'Reforços enemics',
    enemyHint:
      'Es formen fora de la vista, a uns 600–900 m, i van cap allà a tota velocitat. Els jugadors no en reben cap avís.',
    enemySize: 'Mida',
    enemyGroups: (n: number) => (n === 1 ? 'Un grup' : n === 2 ? 'Dos grups' : 'Tres grups'),
    enemyTarget: 'Cap a',
    enemyPoint: 'El punt marcat al mapa',
    enemySend: 'Envia’ls',
    worldMismatch: (player: string, theirs: string, ours: string) =>
      `${player} té carregada una altra illa: ${theirs}, quan el servidor corre ${ours}. Que revisi els mods del seu client.`,
    hcFps: 'FPS del headless client',
    activeGroups: 'Grups actius',
    schedulerLag: 'Retard de l’escalonador',
    addObjective: 'Afegeix un objectiu',
    assign: 'Assigna',
    extendTime: 'Amplia el temps',
    supportsUsedBy: 'Qui l’ha demanat, quan i on',
    sendFromCommand: 'Envia des del comandament',
    addObjectiveHint:
      'Fes clic al mapa per situar-lo, tria el tipus i envia’l: apareix com a tasca als jugadors i al runtime, i queda desat per si el servidor cau.',
    objectiveType: 'Tipus',
    objectivePriority: 'Prioritat',
    objectiveTitle: 'Títol (buit: el del tipus)',
    objectiveRadius: 'Radi (m)',
    objectivePositionHint: 'Fes clic al mapa per situar l’objectiu.',
    objectiveAt: (x: number, y: number) => `Situat a ${x}, ${y}`,
    addObjectiveSend: 'Afegeix l’objectiu',
    cancelObjectiveConfirm: (title: string) => ({
      title: `Cancel·lar «${title}»?`,
      body: 'Els jugadors perden la tasca; no compta ni com a fet ni com a fallat.',
    }),
    cancelObjectiveDo: 'Cancel·la l’objectiu',
    sendNow: 'Envia ara',
    schedule: 'Programa',
    undoWindow: 'Desfés-ho en cinc segons',
    endMissionConfirm: (code: string) =>
      `Per acabar la missió, escriu el codi de la sessió «${code}»:`,
    map: 'Mapa en directe',
    /** An ended night's map, with the session report's layers on it. */
    mapEnded: 'Mapa de la partida',
    mapLayers: 'Capes del mapa',
    mapLayersHint:
      'Com a l’informe de la sessió: per on es van moure els jugadors i on hi va haver baixes. Toca una capa per amagar-la o tornar-la a mostrar.',
    mapMovement: 'Moviment',
    mapDeaths: 'Baixes',
    mapLayerOn: 'visible',
    mapLayerOff: 'amagada',
    mapLayerHide: (layer: string) => `Amaga la capa «${layer}»`,
    mapLayerShow: (layer: string) => `Mostra la capa «${layer}»`,
    mapNoReport: 'El servidor no té informe d’aquesta partida: el mapa només mostra la missió.',
    aiDensity: 'Densitat d’IA',
    playerPositions: 'Posicions dels jugadors',
    /** The page's three movements: what a director watches, what he decides, what he does. */
    watch: 'Vigila',
    decide: 'Decideix',
    act: 'Actua',
    watchHint: 'La salut del servidor, el mapa i el que hi va passant.',
    decideHint: 'Les votacions obertes i el que demana la gent.',
    actHint: 'Tot el que envia una ordre al servidor.',
    notLive: 'La sessió encara no és en joc: les ordres al servidor s’activaran quan comenci.',
    /** The page's three fixed zones: the status strip, the map, the action panel. */
    statusStrip: 'Estat de la sessió',
    actionPanel: 'Panell d’accions',
    actionPanelHint:
      'Tot el que la direcció pot fer durant la partida: objectius, votacions, suports, ràdio i servidor.',
    /** The right-hand column once the night is over: how it ended, nothing to act on. */
    outcomePanel: 'Com ha acabat',
    outcomePanelHint: 'La partida ha acabat: aquí tens com ha quedat cada objectiu.',
    nowGroup: 'Ara mateix',
    ended:
      'La sessió ha acabat: el que veus és el que va passar. Ja no es poden enviar ordres al servidor.',
    stripMinute: 'Minut',
    stripPlayers: 'Jugadors',
    stripAiUnits: 'Unitats d’IA',
    noProblems: 'Sense problemes',
    selfCheckNone: 'Sense autoexamen',
    technical: 'Detalls tècnics',
    upcoming: 'Properament',
    upcomingHint: 'Dissenyat i encara per fer; es mostra perquè sàpigues què vindrà.',
    scheduleMessage: 'Programa un missatge de comandament',
    assignExtend: 'Assigna i amplia el temps d’un objectiu',
  },
  /**
   * A scripted night's phases (engine step E12): live on the direction page, and
   * in the report once the night is over. Only the direction ever sees a phase
   * that has not fired, what fired it, or the rest of the script's events.
   */
  scriptPhases: {
    directionTitle: 'Fases de la nit',
    directionHint:
      'El guió de la missió, fase a fase, tal com el servidor les va disparant. Només ho veu la direcció: els jugadors no en saben res fins que la nit s’acaba.',
    reportTitle: 'Fases',
    reportHint:
      'Com ha anat la nit, fase a fase, amb el minut de la missió en què cada una va començar.',
    none: 'Encara no s’ha disparat cap fase.',
    now: 'ara',
    pending: 'pendent',
    minute: (seconds: number) => `min ${Math.floor(seconds / 60)}`,
    repeats: (n: number) => `×${n}`,
    firedBy: (label: string) => `per ${label}`,
    alarmUp: 'amb l’alarma',
    eventsTitle: 'Esdeveniments del guió',
    event: (name: string) => {
      if (name.startsWith('direction.'))
        return `Ordre de la direcció: ${name.slice('direction.'.length)}`;
      if (name.startsWith('clock.')) return `Rellotge: ${name.slice('clock.'.length)}`;
      if (name.startsWith('objective.')) return `Objectiu: ${name.slice('objective.'.length)}`;
      if (name === 'alarm.up') return 'Alarma';
      return name;
    },
  },
};
